import type { CSSProperties, ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import styled, { css } from 'styled-components'
import { rem } from 'polished'

import LineBarsIcon from 'LuxKit/Icons/line/LineBarsIcon'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'

import { useEventSubscription, useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import type { ChatUiActionData } from 'tripPlanner/api/types'
import CartButton from '../components/CartButton'
import DownloadItineraryButton from '../components/DownloadItineraryButton'
import { useChatUiAction } from '../contexts/ChatUiActionContext'
import TripChatMobileTabs, { type TripChatMobileTabId, type TripChatMobileTabItem } from './TripChatMobileTabs'
import useTripChatMobileViewUrl from './useTripChatMobileViewUrl'
import useTripChatMobileUnreadChat from './useTripChatMobileUnreadChat'
import { useTripChatMobileOverlayOptional } from './TripChatMobileOverlayContext'
import MobileFloatingAskAI from './MobileFloatingAskAI'
import {
  MOBILE_FLOATING_ASK_AI_PREVIEW_SCROLL_PADDING,
  MOBILE_FLOATING_ASK_AI_SCROLL_PADDING,
  MOBILE_FLOATING_ASK_AI_SCROLL_PADDING_VAR,
} from './mobileFloatingAskAILayout'
import Clickable from 'components/Common/Clickable'
import { TripPlannerStyledLogo } from 'tripPlanner/components/Header/Shared'
import PrivateChatBanner from 'tripPlanner/components/PrivateChatBanner'
import CustomerVisibleChatBanner from 'tripPlanner/components/CustomerVisibleChatBanner'
import { selectSpoofBanner } from 'tripPlanner/utils/selectSpoofBanner'
import noop from 'lib/function/noop'

interface TripChatMobileAppShellProps {
  children?: ReactNode
  onMenuOpen?: () => void
  tabPanels?: Partial<Record<TripChatMobileTabId, ReactNode>>
  showTabs?: boolean
  showComposerReservedSpace?: boolean
  showCartButton?: boolean
  isCartButtonDisabled?: boolean
  showDownloadButton?: boolean
  cartItemCount?: number
  cartTotalPrice?: number
  isCartButtonLoading?: boolean
  isSpoofed?: boolean
  hiddenFromCustomer?: boolean
  chatExists?: boolean
  /** Resets banner dismissal on chat change, and identifies the itinerary to export. */
  chatId?: number
  onCreateNewChat?: () => void
  onLogin?: () => void
}

interface TripChatMobileAppShellContextValue {
  activeTab: TripChatMobileTabId
  setActiveTab: (tabId: TripChatMobileTabId) => void
}

const TripChatMobileAppShellContext = createContext<TripChatMobileAppShellContextValue | undefined>(undefined)

const ShellRoot = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-block-start: env(safe-area-inset-top, 0px);
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`

const ShellHeader = styled.header<{ $hasTabs: boolean, $requiresWideTabs: boolean }>`
  display: grid;
  grid-template-columns: ${rem(44)} minmax(0, 1fr) auto;
  grid-template-areas: 'menu brand actions';
  align-items: center;
  flex-shrink: 0;
  min-height: ${rem(60)};
  padding-block: 0;
  padding-inline: ${rem(4)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${props => props.$hasTabs && css`
    grid-template-areas: 'menu tabs actions';

    @media (max-width: ${rem(349)}) {
      grid-template-areas:
        'menu brand actions'
        'tabs tabs tabs';
      grid-template-rows: ${rem(60)} auto;
    }
  `}

  ${props => props.$hasTabs && props.$requiresWideTabs && css`
    @media (max-width: ${rem(439)}) {
      grid-template-areas:
        'menu brand actions'
        'tabs tabs tabs';
      grid-template-rows: ${rem(60)} auto;
    }
  `}
`

const HeaderMenu = styled.div`
  grid-area: menu;
`

const HeaderSpacer = styled.span`
  width: ${rem(44)};
  height: ${rem(44)};
`

const HeaderActions = styled.div`
  grid-area: actions;
  display: flex;
  align-items: center;
  gap: ${rem(4)};
  justify-self: end;
  min-width: 0;
`

const HeaderBrandGroup = styled.div`
  grid-area: brand;
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: ${rem(12)};
  justify-self: start;
`

const HeaderTabs = styled.div`
  grid-area: tabs;
  width: 100%;
  max-width: ${rem(320)};
  min-width: 0;
  justify-self: center;
`

const HeaderBrandLink = styled(Clickable)`
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: ${rem(12)};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};

  &:not(:disabled) {
    &:hover {
      background-color: transparent;
      border-color: transparent;
      color: ${props => props.theme.palette.neutral.surface.normalContrast};
    }
  }
`

const HeaderBrandText = styled(BodyText)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const IconButton = styled.button`
  display: inline-flex;
  width: ${rem(44)};
  height: ${rem(44)};
  align-items: center;
  justify-content: center;
  justify-self: center;
  border: 0;
  border-radius: ${props => props.theme.borderRadius.S};
  background: transparent;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  cursor: pointer;

  &:disabled {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    cursor: default;
  }

  &:focus-visible {
    outline: ${rem(2)} solid ${props => props.theme.palette.highlight.primary.normalForeground};
    outline-offset: ${rem(2)};
  }
`

const HeaderDownloadItineraryButton = styled(DownloadItineraryButton)`
  height: ${rem(38)};
  padding: ${rem(4)};
  border-radius: ${props => props.theme.borderRadius.round};
  border: 0;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const ShellBody = styled.main`
  position: relative;
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  ${MOBILE_FLOATING_ASK_AI_SCROLL_PADDING_VAR}: ${rem(MOBILE_FLOATING_ASK_AI_SCROLL_PADDING)};
`

const TabPanels = styled.div`
  position: relative;
  min-height: 0;
  flex: 1 1 auto;
  overflow: hidden;
`

const KEEP_ALIVE_TAB_CLASS = 'keep-alive'

const TabPanel = styled.div`
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: ${rem(20)};

  &.custom {
    overflow: hidden;
    padding: 0;
  }

  &[hidden] {
    display: none;
  }

  /* Keep the chat scroller laid out so its scrollTop survives tab switches, and
     keep the Mapbox canvas sized to preserve its WebGL context on iOS.
     The UA [hidden] rule is display:none !important, so this override must win. */
  &.${KEEP_ALIVE_TAB_CLASS}[hidden] {
    display: block !important;
    position: absolute;
    inset-block: 0;
    inset-inline: 0;
    visibility: hidden;
    pointer-events: none;
  }

  /* FilterChip and other descendants may set visibility: visible themselves. */
  &.${KEEP_ALIVE_TAB_CLASS}[hidden] * {
    visibility: hidden !important;
  }
`

const PlaceholderView = styled.div`
  display: flex;
  min-height: 100%;
  flex-direction: column;
  justify-content: center;
  gap: ${rem(8)};
  text-align: center;
`

const ComposerReservedSpace = styled.footer`
  min-height: calc(${rem(68)} + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const DEFAULT_TAB_PANELS: Partial<Record<TripChatMobileTabId, ReactNode>> = {}

interface TripChatMobileTabPanelProps {
  activeTab: TripChatMobileTabId
  labels: ReturnType<typeof useTripChatMobileLabels>
  tab: TripChatMobileTabItem
  tabPanels: Partial<Record<TripChatMobileTabId, ReactNode>>
}

function TripChatMobileTabPanel({ activeTab, labels, tab, tabPanels }: TripChatMobileTabPanelProps) {
  const panelContent = tabPanels[tab.id]
  const hasPanelContent = panelContent !== undefined

  return (
    <TabPanel
      className={clsx(hasPanelContent && 'custom', (tab.id === 'chat' || tab.id === 'map') && KEEP_ALIVE_TAB_CLASS)}
      data-testid={`trip-chat-mobile-tab-panel-${tab.id}`}
      hidden={activeTab !== tab.id}
    >
      {hasPanelContent && panelContent}
      {!hasPanelContent && (
        <PlaceholderView>
          <Heading variant="heading5" as="h2">
            {tab.label}
          </Heading>
          <BodyText variant="medium">
            {labels.placeholders[tab.id]}
          </BodyText>
        </PlaceholderView>
      )}
    </TabPanel>
  )
}

function useTripChatMobileLabels() {
  const intl = useIntl()

  return useMemo(() => ({
    menuButton: intl.formatMessage({
      id: 'Rh5EB1l_8dTFdLNrgEnU',
      defaultMessage: 'Open trip menu',
      description: 'Accessibility label for the mobile trip planner menu button in the app shell header',
    }),
    aiTripPlanner: intl.formatMessage({
      id: 'EYQsWpC_SwW6ZS0Xb0Yz',
      defaultMessage: 'AI TripPlanner',
      description: 'Branding label for the AI TripPlanner feature in the header',
    }),
    cartButton: intl.formatMessage({
      id: 'gUgzcjMkoSgOtM_qF0I3',
      defaultMessage: 'Open trip cart',
      description: 'Accessibility label for the mobile trip cart button in the app shell header',
    }),
    login: intl.formatMessage({
      id: '0yQ2UDZporNc794dxiNY',
      defaultMessage: 'Log in',
      description: 'Button text in the mobile TripPlanner landing header that opens the account access modal',
    }),
    tabList: intl.formatMessage({
      id: 'HwL8PoaVwNML5Nh72udF',
      defaultMessage: 'Trip planner mobile views',
      description: 'Accessibility label for the mobile trip planner tab navigation control',
    }),
    tabs: {
      chat: intl.formatMessage({
        id: 'W6AYq4RaOfBu_SyMN7Vv',
        defaultMessage: 'Chat',
        description: 'Tab label for the mobile AI TripPlanner chat conversation view',
      }),
      list: intl.formatMessage({
        id: '9_P2Kl3YWpDSfAH5PcFx',
        defaultMessage: 'Itinerary',
        description: 'Tab label for the mobile AI TripPlanner itinerary list view',
      }),
      map: intl.formatMessage({
        id: 'IASdasfMP-rhaQoHcb5w',
        defaultMessage: 'Map',
        description: 'Tab label for the mobile AI TripPlanner trip map view',
      }),
    },
    placeholders: {
      chat: intl.formatMessage({
        id: 'EwQ3FblF7olWsyrdOhnq',
        defaultMessage: 'Your planning chat will appear here.',
        description: 'Passive placeholder text shown before the mobile chat view is wired',
      }),
      list: intl.formatMessage({
        id: '1mQR6iQWYoQO8jSRvX6z',
        defaultMessage: 'Your trip list will appear here.',
        description: 'Passive placeholder text shown before the mobile list view is wired',
      }),
      map: intl.formatMessage({
        id: '_zTqwDdDiH4vhnUY86fd',
        defaultMessage: 'Your trip map will appear here.',
        description: 'Passive placeholder text shown before the mobile map view is wired',
      }),
    },
  }), [intl])
}

function TripChatMobileAppShell({
  children,
  onMenuOpen,
  tabPanels = DEFAULT_TAB_PANELS,
  showTabs = true,
  showComposerReservedSpace = true,
  showCartButton = false,
  isCartButtonDisabled = false,
  showDownloadButton = false,
  cartItemCount = 0,
  cartTotalPrice = 0,
  isCartButtonLoading = false,
  isSpoofed = false,
  hiddenFromCustomer = true,
  chatExists = false,
  chatId,
  onCreateNewChat = noop,
  onLogin,
}: TripChatMobileAppShellProps) {
  const labels = useTripChatMobileLabels()
  const eventBus = useTripPlannerEventBus()
  const mobileOverlay = useTripChatMobileOverlayOptional()
  const { pendingUiAction, clearPendingUiAction } = useChatUiAction()
  const { activeTab, setActiveTab } = useTripChatMobileViewUrl()
  const hasUnreadChat = useTripChatMobileUnreadChat({ activeTab, chatId })
  const [isPrivateChatBannerDismissed, setIsPrivateChatBannerDismissed] = useState(false)
  const [isCustomerVisibleChatBannerDismissed, setIsCustomerVisibleChatBannerDismissed] = useState(false)
  const spoofBanner = selectSpoofBanner({ isSpoofed, hiddenFromCustomer, chatExists })
  const resolvedActiveTab = showTabs ? activeTab : 'chat'
  // Ownership and visibility are separate: the floating composer keeps owning the Ask AI
  // input for List while an overlay covers it, so a context chip it holds
  // survives a drawer, sheet or cart panel opening over the tab.
  const ownsFloatingAskAI = showTabs && resolvedActiveTab === 'list'
  const isFloatingAskAIHidden = !!mobileOverlay?.hasOpenOverlay
  const isFloatingAskAIVisible = ownsFloatingAskAI && !isFloatingAskAIHidden
  const [isFloatingPreviewActive, setIsFloatingPreviewActive] = useState(false)
  const floatingAskAIScrollPadding = isFloatingPreviewActive
    ? MOBILE_FLOATING_ASK_AI_PREVIEW_SCROLL_PADDING
    : MOBILE_FLOATING_ASK_AI_SCROLL_PADDING

  // A dismissal on one chat shouldn't hide the banner on a different chat.
  useEffect(() => {
    setIsPrivateChatBannerDismissed(false)
    setIsCustomerVisibleChatBannerDismissed(false)
  }, [chatId])

  useEffect(() => {
    if (!isFloatingAskAIVisible) {
      setIsFloatingPreviewActive(false)
    }
  }, [isFloatingAskAIVisible])

  const shellBodyStyle = useMemo(() => ({
    [MOBILE_FLOATING_ASK_AI_SCROLL_PADDING_VAR]: rem(floatingAskAIScrollPadding),
  } as CSSProperties), [floatingAskAIScrollPadding])

  const applyPlanViewUiAction = useCallback((data: ChatUiActionData) => {
    if (data.action !== 'set_right_panel_view') return false

    const nextTab = data.view
    if (nextTab !== 'list') return false

    setActiveTab(nextTab)
    clearPendingUiAction()
    return true
  }, [clearPendingUiAction, setActiveTab])

  const handleFocusChat = useCallback(() => {
    setActiveTab('chat')
  }, [setActiveTab])

  useEventSubscription('ui_action', (event) => {
    applyPlanViewUiAction(event.data)
  })

  useEventSubscription('focus_chat', handleFocusChat)

  useEventSubscription('view_itinerary_item', () => {
    setActiveTab('list')
  })

  useEffect(() => {
    if (!pendingUiAction) return
    applyPlanViewUiAction(pendingUiAction)
  }, [applyPlanViewUiAction, pendingUiAction])

  const tabs = useMemo<ReadonlyArray<TripChatMobileTabItem>>(() => [
    { id: 'chat', label: labels.tabs.chat },
    { id: 'list', label: labels.tabs.list },
    { id: 'map', label: labels.tabs.map },
  ], [labels.tabs])

  const contextValue = useMemo<TripChatMobileAppShellContextValue>(() => ({
    activeTab: resolvedActiveTab,
    setActiveTab,
  }), [resolvedActiveTab, setActiveTab])

  const handleCartClick = useCallback(() => {
    eventBus.dispatch({ type: 'open_cart_panel' })
  }, [eventBus])

  const visibleTabs = useMemo(() => {
    if (showTabs) return tabs

    return tabs.filter(tab => tab.id === 'chat')
  }, [showTabs, tabs])

  // The tabs drop to their own row once the actions column stops being a single
  // narrow icon — either the cart has widened to show a price, or the download
  // button sits beside it.
  const requiresWideTabs = (showCartButton && !isCartButtonDisabled && (cartItemCount > 0 || isCartButtonLoading)) ||
    (showDownloadButton && showCartButton)

  const handleTabChange = useCallback((tabId: TripChatMobileTabId) => {
    if (tabId !== activeTab && activeTab === 'chat' && document.activeElement instanceof HTMLTextAreaElement) {
      document.activeElement.blur()
    }

    setActiveTab(tabId)
  }, [activeTab, setActiveTab])

  const tabPanelElements = useMemo(() => visibleTabs.map(tab => (
    <TripChatMobileTabPanel
      key={tab.id}
      activeTab={showTabs ? activeTab : 'chat'}
      labels={labels}
      tab={tab}
      tabPanels={tabPanels}
    />
  )), [activeTab, labels, showTabs, tabPanels, visibleTabs])

  return (
    <TripChatMobileAppShellContext.Provider value={contextValue}>
      <ShellRoot data-testid="trip-chat-mobile-app-shell">
        {spoofBanner === 'private' && !isPrivateChatBannerDismissed && (
          <PrivateChatBanner onDismiss={() => setIsPrivateChatBannerDismissed(true)} />
        )}
        {spoofBanner === 'customerVisible' && !isCustomerVisibleChatBannerDismissed && (
          <CustomerVisibleChatBanner
            onCreateNewChat={onCreateNewChat}
            onDismiss={() => setIsCustomerVisibleChatBannerDismissed(true)}
          />
        )}
        <ShellHeader $hasTabs={showTabs} $requiresWideTabs={requiresWideTabs}>
          <HeaderMenu>
            {onMenuOpen && (
              <IconButton
                type="button"
                aria-label={labels.menuButton}
                onClick={onMenuOpen}
              >
                <LineBarsIcon size="S" />
              </IconButton>
            )}
            {!onMenuOpen && <HeaderSpacer aria-hidden="true" />}
          </HeaderMenu>
          {!showTabs && (
            <HeaderBrandGroup>
              <HeaderBrandLink to="/" aria-label={labels.aiTripPlanner}>
                <TripPlannerStyledLogo data-testid="trip-chat-mobile-header-logo" />
                <HeaderBrandText variant="large" weight="bold">
                  {labels.aiTripPlanner}
                </HeaderBrandText>
              </HeaderBrandLink>
            </HeaderBrandGroup>
          )}
          {showTabs && (
            <HeaderTabs>
              <TripChatMobileTabs
                activeTab={activeTab}
                ariaLabel={labels.tabList}
                tabs={tabs}
                onTabChange={handleTabChange}
                hasUnreadChat={hasUnreadChat}
                inlineOnWideScreens
              />
            </HeaderTabs>
          )}
          <HeaderActions>
            {showDownloadButton && (
              <HeaderDownloadItineraryButton chatId={chatId} iconOnly />
            )}
            {showCartButton && !isCartButtonDisabled && (
              <CartButton
                itemCount={cartItemCount}
                totalPrice={cartTotalPrice}
                isLoading={isCartButtonLoading}
                onClick={handleCartClick}
              />
            )}
            {showCartButton && isCartButtonDisabled && (
              <IconButton
                type="button"
                aria-label={labels.cartButton}
                disabled={isCartButtonDisabled}
                onClick={handleCartClick}
              >
                <LineShoppingCartIcon size="S" />
              </IconButton>
            )}
            {onLogin && (
              <TextButton kind="secondary" size="medium" onClick={onLogin}>
                {labels.login}
              </TextButton>
            )}
            {!showCartButton && !showDownloadButton && !onLogin && (
              <HeaderSpacer aria-hidden="true" />
            )}
          </HeaderActions>
        </ShellHeader>
        <ShellBody style={shellBodyStyle}>
          <TabPanels>
            {tabPanelElements}
          </TabPanels>
          <MobileFloatingAskAI
            isActive={ownsFloatingAskAI}
            isHidden={isFloatingAskAIHidden}
            activeTab={resolvedActiveTab}
            onPreviewActiveChange={setIsFloatingPreviewActive}
          />
          {showComposerReservedSpace && (
            <ComposerReservedSpace
              aria-hidden="true"
              data-testid="trip-chat-mobile-composer-reserved-space"
            />
          )}
        </ShellBody>
        {children}
      </ShellRoot>
    </TripChatMobileAppShellContext.Provider>
  )
}

export function useTripChatMobileAppShell() {
  const context = useContext(TripChatMobileAppShellContext)

  if (!context) {
    throw new Error('useTripChatMobileAppShell must be used within TripChatMobileAppShell')
  }

  return context
}

export default TripChatMobileAppShell
